import { Availability } from "../pages/types";

export interface SignUpValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  passwordConfirmation: string;
  acceptTermsAndConditions: boolean;
  accountType: string;
}

export enum AccountType {
  client = "CLIENT",
  store = "STORE",
  staff = "STAFF",
}

export interface UserDetail {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  documentId: string;
  currency?: Currency;
  avatarUrl?: string;
  stripeId?: string;
  stripeConnects?: StripeConnect[];
  accountType: AccountType;
  businessId?: string;
  isBusinessPage?: boolean;
  isStaff?: boolean;
  isAdmin?: boolean;
}

interface ConnectedStore {
  storeId: string;
  isLoyalClient?: boolean;
}

export interface ClientDetails {
  clientUserId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  documentId: string;
  connectedStore?: ConnectedStore[];
  storeIds: string[];
}

interface StripeConnect {
  state: string; // the CRSF unique code uuid in this case
  stripeUserId: string;
  userId: string;
}

export interface ConnectedAccount {
  id: string;
  country: string;
  default_currency: string;
  email: string;
  settings: { dashboard: { display_name: string; timezone: string } };
}

export const currencyAllowed = ["AUD", "CAD", "EUR", "GBP", "NGN", "USD"];

export enum Currency {
  AUD = "AUD",
  CAD = "CAD",
  EUR = "EUR",
  GBP = "GBP",
  NGN = "NGN",
  USD = "USD",
}

export const MapCurrencyToSymbol = {
  [Currency.AUD]: "$",
  [Currency.CAD]: "CA$",
  [Currency.EUR]: "€",
  [Currency.GBP]: "£",
  [Currency.NGN]: "₦",
  [Currency.USD]: "$",
};

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: PermissionCategory;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum PermissionCategory {
  BOOKING = "Booking Management",
  CLIENT = "Client Management",
  STAFF = "Staff Management",
  INVENTORY = "Inventory Management",
  COMMUNICATION = "Communication",
  ANALYTICS = "Analytics & Reporting",
  SETTINGS = "System Settings",
  CONTENT = "Content Management",
}

export interface BusinessAssignment {
  businessId: string;
  role: StaffRole;
  permissions: Record<string, boolean>;
  isActive: boolean;
  assignedAt: Date;
  updatedAt: Date;
}

export interface StaffMember {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  businessAssignment: BusinessAssignment | null;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  staffColor?: string;
  staffId: string;
  availability: Availability;
  daysOff?: string[];
}

export enum StaffRole {
  STAFF = "STAFF",
  ADMIN = "ADMIN",
  MANAGER = "MANAGER",
}

export interface StaffRolePermissions {
  [key: string]: Record<string, boolean>;
}

export const DefaultRolePermissions: StaffRolePermissions = {
  [StaffRole.ADMIN]: {
    // Dashboard Management
    viewDashboardCards: true,
    viewDashboardCalender: true,

    // Booking Management
    createBooking: true,
    editBooking: true,
    cancelBooking: true,
    deleteBooking: true,
    viewAllBookings: true,

    // Client Management
    createClient: true,
    editClient: true,
    deleteClient: true,
    viewClientDetails: true,

    // Page Management
    createPage: true,
    editPage: true,
    deletePage: true,
    viewAllPages: true,

    // Service Management
    createService: true,
    editService: true,
    deleteService: true,
    createServiceCategory: true,
    editServiceCategory: true,
    deleteServiceCategory: true,

    // Staff Management
    viewStaffs: true,
    editStaffDetails: true,
    addStaffMember: true,
    updateStaffRole: true,
    deactivateStaff: true,

    // Permission Management
    viewPermissions: true,
    createNewPermissionRole: true,
    updatePermissionRole: true,

    // Payment Management
    addNewPaymentMethod: true,
    deletePaymentMethod: true,
    viewPaymentMethods: true,
  },
  [StaffRole.MANAGER]: {
    // Dashboard Management
    viewDashboardCalender: true,
    viewDashboardCards: true,

    // Booking Management
    createBooking: true,
    editBooking: true,
    cancelBooking: true,
    deleteBooking: true,
    viewAllBookings: true,

    // Client Management
    createClient: true,
    editClient: true,
    deleteClient: true,
    viewClientDetails: true,

    // Page Management
    createPage: true,
    editPage: true,
    deletePage: true,
    viewAllPages: true,

    // Service Management
    createService: true,
    editService: true,
    deleteService: true,
    createServiceCategory: true,
    editServiceCategory: true,
    deleteServiceCategory: true,

    // Staff Management
    viewStaffs: true,
    editStaffDetails: true,
    addStaffMember: true,
    updateStaffRole: true,
    deactivateStaff: true,

    // Permission Management
    viewPermissions: false,
    createNewPermissionRole: false,
    updatePermissionRole: false,

    // Payment Management
    addNewPaymentMethod: true,
    deletePaymentMethod: true,
    viewPaymentMethods: true,
  },
  [StaffRole.STAFF]: {
    // Dashboard Management
    viewDashboardCalender: true,
    viewDashboardCards: true,

    // Booking Management
    createBooking: true,
    editBooking: true,
    cancelBooking: true,
    deleteBooking: false,
    viewAllBookings: true,

    // Client Management
    createClient: true,
    editClient: true,
    deleteClient: false,
    viewClientDetails: true,

    // Page Management
    createPage: false,
    editPage: false,
    deletePage: false,
    viewAllPages: true,

    // Service Management
    createService: false,
    editService: false,
    deleteService: false,
    createServiceCategory: false,
    editServiceCategory: false,
    deleteServiceCategory: false,

    // Staff Management
    viewStaffs: false,
    editStaffDetails: false,
    addStaffMember: false,
    updateStaffRole: false,
    deactivateStaff: false,

    // Permission Management
    viewPermissions: false,
    createNewPermissionRole: false,
    updatePermissionRole: false,

    // Payment Management
    addNewPaymentMethod: false,
    deletePaymentMethod: false,
    viewPaymentMethods: false,
  },
};
